import { useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react"
import { getBidSection } from "@/lib/bid-sections"
import { useBidsStore } from "@/stores/bids-store"
import { useDocumentsStore } from "@/stores/documents-store"
import { useExtractionStore } from "@/stores/extraction-store"
import { useReconciliationStore } from "@/stores/reconciliation-store"
import { useTenderOverviewStore } from "@/stores/tender-overview-store"
import { useBidMembersStore, type BidMemberSummary } from "@/stores/bid-members-store"
import { BidHeader } from "@/components/bid-menu/BidHeader"
import { AnalyseBidButton, useHasCompletedAnalysis } from "@/components/bid-menu/AnalyseBidButton"
import { BidDocumentsTab } from "@/components/bid-documents/BidDocumentsTab"
import { BidOverviewSection } from "@/components/BidOverviewSection"
import { QualifySection } from "@/components/bid-areas/QualifySection"
import { PlanSection } from "@/components/bid-areas/PlanSection"
import { WriteSection } from "@/components/bid-areas/WriteSection"
import { ReviewSection } from "@/components/bid-areas/ReviewSection"
import { SubmitSection } from "@/components/bid-areas/SubmitSection"
import { TeamSection } from "@/components/TeamSection"
import { ActivitySection } from "@/components/ActivitySection"
import { useDocumentProcessingPoll } from "@/hooks/useDocumentProcessingPoll"
import { cn } from "@/lib/utils"
import { useBidReadOnly } from "@/hooks/useViewOnly"

/** These sections keep their controls outside their own scrolling content. */
const SECTIONS_WITH_OWN_SCROLL = new Set(["qualify", "plan", "submit", "activity"])

/** Stable empty list: a fresh `[]` from a selector re-renders forever (getSnapshot warning). */
const NO_MEMBERS = Object.freeze([]) as readonly BidMemberSummary[] as BidMemberSummary[]

export function BidDetailsPage() {
  const { bidId } = useParams<{ bidId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const activeSection = getBidSection(location.pathname)

  const { selectedBid, error, fetchBidById, clearError } = useBidsStore()
  // Only this URL's bid counts. Until its fetch settles the page is loading —
  // never "not found". The store's `isLoading` getter is copied by value on
  // every set, so it reads one update behind: it was still false while the
  // fetch ran, which flashed "Bid Not Found" on first open (and could stick).
  const bid = selectedBid?.id === bidId ? selectedBid : null
  const { documents, fetchDocuments, hasProcessingDocuments, getProcessedDocumentCount } = useDocumentsStore()
  const loadAllCategories = useExtractionStore((state) => state.loadAllCategories)
  const loadReconciliationRuns = useReconciliationStore((state) => state.loadRuns)
  const loadTenderOverview = useTenderOverviewStore((state) => state.load)
  const members = useBidMembersStore((s) => (s.bidId === bidId ? s.members : NO_MEMBERS))
  const loadMembers = useBidMembersStore((s) => s.load)
  const hasCompletedAnalysis = useHasCompletedAnalysis()

  // Fetch the bid and all analysis data on mount or when bidId changes, so
  // every work area opens instantly.
  useEffect(() => {
    if (bidId) {
      void fetchBidById(bidId)
      void fetchDocuments(bidId)
      void loadAllCategories(bidId)
      // Hydrate the tender-overview store so "Analyse bid" knows whether a
      // detailed Tender Summary already exists wherever it is opened from.
      void loadTenderOverview(bidId)
      // Hydrate reconciliation runs so Qualify's menu count shows the pending
      // count from any page. Best-effort — swallow errors like category loading.
      void Promise.resolve(loadReconciliationRuns(bidId)).catch(() => {})
      void loadMembers(bidId)
    }
  }, [bidId, fetchBidById, fetchDocuments, loadAllCategories, loadReconciliationRuns, loadTenderOverview, loadMembers])

  // Poll while documents are processing (store owns the interval)
  useDocumentProcessingPoll(bidId)

  // An org viewer, or a member who is not on this bid (the server's canEdit).
  const { isReadOnly: isReadOnlyBidView, message: readOnlyMessage } = useBidReadOnly(bidId)
  const isDocumentsProcessing = hasProcessingDocuments()
  const processedCount = getProcessedDocumentCount()
  const totalCount = documents.length

  // Show loading state — until the bid arrives or its fetch fails
  if (!bid && !error) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Loading bid details...</p>
      </div>
    )
  }

  // Show error state
  if (error && !bid) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="mt-2 text-sm text-destructive">{error}</p>
        <div className="flex gap-2 mt-4">
          <Button variant="ghost" onClick={() => void navigate("/app/bids")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Bids
          </Button>
          <Button variant="outline" onClick={() => { clearError(); if (bidId) void fetchBidById(bidId) }}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  // Unreachable at runtime (the two branches above cover a missing bid); kept
  // so TypeScript narrows `bid` below.
  if (!bid) return null


  // "Analyse bid" lives on Qualify. Overview keeps it only until the first
  // analysis has produced something, as the bid's obvious first step.
  const showAnalyse = activeSection === "qualify" || (activeSection === "overview" && !hasCompletedAnalysis)
  const hasOwnScroll = SECTIONS_WITH_OWN_SCROLL.has(activeSection)

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {isReadOnlyBidView && (
        <div className="shrink-0 bg-amber-50 border-b border-amber-200 px-6 py-3">
          <p className="text-sm font-medium text-amber-900">
            {/* Being added to the bid gives a member edit access; it does nothing for a viewer. */}
            {readOnlyMessage}
          </p>
        </div>
      )}

      {/* Processing Documents Banner */}
      {isDocumentsProcessing && totalCount > 0 && (
        <div className="shrink-0 bg-muted/50 border-b border-border px-6 py-3 flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">
              Processing documents...
            </p>
            <p className="text-meta">
              {processedCount} of {totalCount} documents ready for analysis
            </p>
          </div>
        </div>
      )}

      <BidHeader
        bid={bid}
        members={members}
        readOnly={isReadOnlyBidView}
        actions={showAnalyse ? <AnalyseBidButton bid={bid} readOnly={isReadOnlyBidView} /> : undefined}
        onEdited={() => bidId && void fetchBidById(bidId)}
        onDeleted={() => void navigate("/app/bids")}
      />

      <div className={cn("min-h-0 flex-1", hasOwnScroll ? "overflow-hidden" : "overflow-auto px-6 py-4")}>
        {activeSection === "overview" && <BidOverviewSection bidId={bidId!} />}
        {activeSection === "qualify" && <QualifySection bidId={bidId!} readOnly={isReadOnlyBidView} />}
        {activeSection === "plan" && <PlanSection bidId={bidId!} readOnly={isReadOnlyBidView} />}
        {activeSection === "write" && <WriteSection bidId={bidId!} />}
        {activeSection === "review" && <ReviewSection bidId={bidId!} />}
        {activeSection === "submit" && <SubmitSection bidId={bidId!} readOnly={isReadOnlyBidView} />}
        {activeSection === "files" && <BidDocumentsTab bidId={bidId!} readOnly={isReadOnlyBidView} />}
        {activeSection === "team" && <TeamSection bidId={bidId!} isReadOnly={isReadOnlyBidView} />}
        {activeSection === "activity" && <ActivitySection bidId={bidId!} />}
      </div>
    </div>
  )
}
